import { Pages } from 'constants/menu';
import { AddSpecialConditionButton } from './modals/specialConditions/AddSpecialConditionButton';
import { useSelectedCampaign, useSelectedContract } from 'hooks';
import { EnsureContractInformationTasksRequestScope } from '@urbanx/agx.activities.client';
import {
  CampaignDetailModelState,
  FormCondition,
  type LinkedDateImpact,
  SpecialConditionData,
  StaffPreviewLinkedDatesRequestSourceDateType,
  StaffSpecialConditionsResponse,
  useConstructConditionClauseText,
  useInvalidateUseStaffGetContractDates,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetSpecialConditions,
  useStaffPreviewLinkedDates,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client';
import { AgxEmptyState, useToast } from '@urbanx/agx.ui-components2';
import { useQueryClient } from '@tanstack/react-query';
import SpecialConditionCollapsiblePanel from './panels/SpecialConditionCollapsiblePanel';
import { CrmDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/CrmDataUpdateHelper';
import { isEqual } from 'lodash-es';
import useCrmDisconnectedModal from 'pages/campaigns/components/manageCampaign/useCrmDisconnectedModal';
import {
  isActionableSpecialCondition,
  withUpdatedSpecialConditionDueDate,
} from './specialConditionDueDate';
import { prepareCustomSpecialConditionsForSave } from './modals/specialConditions/customSpecialConditionEditor';
import { ContractInformationPage } from './ContractInformationPage';
import { useContractInformationEditor } from './useContractInformationEditor';
import { useContractInformationTaskSynchronization } from './useContractInformationTaskSynchronization';
import {
  LinkedDateWarningModal,
  presentCompletedTaskConflicts,
} from './modals/LinkedDateWarningModal';
import { toSpecialConditionsTaskProposal } from './contractInformationTaskProposals';

const SpecialConditions = () => {
  const { campaign } = useSelectedCampaign();
  const { contract, campaignId, isTaskWorkflowEnabled } = useSelectedContract();

  const {
    data: specialConditionsResponse,
    isPending,
    isError,
  } = useStaffGetSpecialConditions(campaignId ?? '', contract?.contractId ?? '');
  const invalidateStaffGetSpecialConditions = useInvalidateUseStaffGetSpecialConditions();
  const invalidateStaffGetContractDates = useInvalidateUseStaffGetContractDates();
  const queryClient = useQueryClient();

  const { modal: crmDisconnectedModal, openWithChanges } = useCrmDisconnectedModal();

  const { pushToast } = useToast();

  const { constructConditionClauseText } = useConstructConditionClauseText({
    onSuccess: () => {},
    onError: () => {},
  });

  const { staffUpdateContractSpecialConditions } = useStaffUpdateContractSpecialConditions({
    onSuccess: () => {},
    onError: () => {},
  });

  const { staffPreviewLinkedDates } = useStaffPreviewLinkedDates({
    onSuccess: () => {},
    onError: () => {},
  });

  type SpecialConditionsDraft = {
    conditions: SpecialConditionData[];
    renderedConditions: NonNullable<StaffSpecialConditionsResponse['renderedConditions']>;
  };

  type SpecialConditionsConfirmation = {
    impactedDates: LinkedDateImpact[];
    sourceDateTypes: StaffPreviewLinkedDatesRequestSourceDateType[];
    taskConflicts: ReturnType<typeof presentCompletedTaskConflicts>;
  };

  const toDraft = (response: StaffSpecialConditionsResponse): SpecialConditionsDraft => ({
    conditions: response.specialConditionsData ?? [],
    renderedConditions: response.renderedConditions ?? [],
  });

  const source: SpecialConditionsDraft | undefined = specialConditionsResponse
    ? toDraft(specialConditionsResponse)
    : undefined;

  const taskSynchronization = useContractInformationTaskSynchronization({
    campaignId,
    contractId: contract?.contractId,
    scope: EnsureContractInformationTasksRequestScope.SpecialConditions,
    isTaskWorkflowEnabled,
    canonicalRevision: JSON.stringify(source),
  });

  const editor = useContractInformationEditor<
    SpecialConditionsDraft,
    SpecialConditionsConfirmation
  >({
    resourceKey: `${campaignId ?? ''}:${contract?.contractId ?? ''}:special-conditions`,
    source,
    isPending,
    isError,
    preflight: async ({ baseline, draft }) => {
      const previewLinkedDates = async () => {
        if (campaign?.state !== CampaignDetailModelState.QLD) return [];

        const dueDates = (conditions: SpecialConditionsDraft['renderedConditions']) => {
          const dates = new Map<string, string | undefined>();

          for (const condition of conditions) {
            if (condition.id && condition.directDueDate !== undefined) {
              dates.set(condition.id, condition.directDueDate);
            }
            for (const clause of condition.clauses ?? []) {
              if (clause.clauseId) dates.set(clause.clauseId, clause.dueDate);
            }
          }

          return dates;
        };

        const baselineDueDates = dueDates(baseline.renderedConditions);
        const draftDueDates = dueDates(draft.renderedConditions);
        const changedClauseIds = [
          ...new Set([...baselineDueDates.keys(), ...draftDueDates.keys()]),
        ].filter((clauseId) => baselineDueDates.get(clauseId) !== draftDueDates.get(clauseId));

        const previews = await Promise.all(
          changedClauseIds.map((specialConditionClauseId) =>
            staffPreviewLinkedDates({
              contractId: contract!.contractId,
              body: {
                sourceDateType: StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate,
                proposedDate: draftDueDates.get(specialConditionClauseId),
                specialConditionClauseId,
              },
            })
          )
        );

        return [
          ...new Map<string, LinkedDateImpact>(
            previews
              .flatMap((preview) => preview?.impactedDates ?? [])
              .map((impact): [string, LinkedDateImpact] => {
                if (!impact.key) throw new Error('Linked date impact key is required');

                return [impact.key, impact];
              })
          ).values(),
        ];
      };

      const specialConditionsProposal = toSpecialConditionsTaskProposal(
        prepareCustomSpecialConditionsForSave(draft.conditions)
      );
      const [impactedDates, completedTaskConflicts] = await Promise.all([
        previewLinkedDates(),
        isTaskWorkflowEnabled
          ? taskSynchronization.previewConflicts({ specialConditionsProposal })
          : Promise.resolve([]),
      ]);
      const taskConflicts = presentCompletedTaskConflicts(completedTaskConflicts);

      return impactedDates.length > 0 || taskConflicts.length > 0
        ? {
            impactedDates,
            sourceDateTypes: [StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate],
            taskConflicts,
          }
        : undefined;
    },
    persist: async ({ baseline, draft }) => {
      const conditions = prepareCustomSpecialConditionsForSave(draft.conditions);
      const response = await staffUpdateContractSpecialConditions({
        contractId: contract!.contractId,
        body: conditions,
      });

      if (!response?.success) return { accepted: false, errors: response?.errors ?? [] };
      if (!response.specialConditions) throw new Error('Canonical special conditions are required');

      const canonicalSpecialConditions: StaffSpecialConditionsResponse = {
        ...response.specialConditions,
        specialConditionsData: response.specialConditions.specialConditionsData ?? [],
        specialConditionIdToClauseNumberMap:
          response.specialConditions.specialConditionIdToClauseNumberMap ?? {},
        renderedConditions: response.specialConditions.renderedConditions ?? [],
      };

      queryClient.setQueryData<StaffSpecialConditionsResponse>(
        ['staffGetSpecialConditions', campaignId!, contract!.contractId],
        canonicalSpecialConditions
      );

      openWithChanges(
        !isEqual(conditions, baseline.conditions)
          ? [
              {
                field: CrmDisconnectedModalFields.ExpectedUnconditionalDate,
                value: '',
              },
            ]
          : []
      );

      return { accepted: true, baseline: toDraft(canonicalSpecialConditions) };
    },
    refresh: {
      authority: 'mutation',
      refreshSource: () => invalidateStaffGetSpecialConditions(campaignId!, contract!.contractId),
      refreshDependencies:
        campaign?.state === CampaignDetailModelState.QLD
          ? [() => invalidateStaffGetContractDates(contract!.contractId)]
          : [],
    },
    synchronization: taskSynchronization.synchronization,
  });

  if (!editor.draft) {
    return (
      <ContractInformationPage currentPage={Pages.SPECIAL_CONDITIONS} editor={editor}>
        {null}
      </ContractInformationPage>
    );
  }

  const { conditions: localSpecialConditions, renderedConditions } = editor.draft;

  const onConditionDeleted = (conditionId: string) => {
    if (!conditionId) return;

    const updatedConditions = localSpecialConditions.filter(
      (condition) => condition.id !== conditionId
    );

    editor.update((current) => ({
      conditions: updatedConditions,
      renderedConditions: current.renderedConditions.filter(
        (condition) => condition.id !== conditionId
      ),
    }));
  };

  const onAddCustomCondition = async (customSpecialCondition: SpecialConditionData) => {
    const updatedConditions = prepareCustomSpecialConditionsForSave([
      ...localSpecialConditions,
      customSpecialCondition,
    ]);

    try {
      const rendered = await constructConditionClauseText({
        campaignId: campaignId ?? '',
        contractId: contract?.contractId ?? '',
        model: updatedConditions,
      });
      editor.update(() => ({ conditions: updatedConditions, renderedConditions: rendered }));
      return true;
    } catch {
      pushToast({
        message: 'Failed to construct condition text blocks',
        type: 'error',
      });
      return false;
    }
  };

  const onDueDateUpdated = async (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => {
    const updatedConditions = prepareCustomSpecialConditionsForSave(
      localSpecialConditions.map((condition) => {
        if (condition.id !== conditionId) return condition;

        return withUpdatedSpecialConditionDueDate(condition, clauseId, newDueDateCondition);
      })
    );

    editor.update((current) => ({
      conditions: updatedConditions,
      renderedConditions: current.renderedConditions.map((renderedCondition) => {
        if (renderedCondition.id !== conditionId) return renderedCondition;

        return {
          ...renderedCondition,
          directDueDate: conditionId === clauseId ? newDueDate : renderedCondition.directDueDate,
          clauses: renderedCondition.clauses?.map((clause) => {
            if (clause.clauseId !== clauseId) return clause;

            return {
              ...clause,
              dueDate: newDueDate,
            };
          }),
        };
      }),
    }));
  };

  const visibleRenderedSpecialConditions = renderedConditions.filter(isActionableSpecialCondition);

  return (
    <ContractInformationPage currentPage={Pages.SPECIAL_CONDITIONS} editor={editor}>
      {visibleRenderedSpecialConditions.length === 0 ? (
        <AgxEmptyState variant="minimal" label="No special conditions added." />
      ) : null}

      {visibleRenderedSpecialConditions.map((renderedCondition) => (
        <SpecialConditionCollapsiblePanel
          renderedSpecialCondition={renderedCondition}
          specialCondition={localSpecialConditions.find(
            (condition) => condition.id === renderedCondition.id
          )}
          onDeleteCondition={onConditionDeleted}
          key={renderedCondition.id ?? renderedCondition.conditionType}
          onDueDateUpdated={onDueDateUpdated}
        />
      ))}
      <AddSpecialConditionButton
        key="add-special-condition-button"
        id="add-special-condition-button"
        label="Add Special Conditions"
        existingConditions={localSpecialConditions}
        renderedConditions={renderedConditions}
        onAddSpecialCondition={onAddCustomCondition}
        campaignId={campaignId ?? ''}
      />
      {editor.confirmation && (
        <LinkedDateWarningModal
          impactedDates={editor.confirmation.impactedDates}
          sourceDateTypes={editor.confirmation.sourceDateTypes}
          taskConflicts={editor.confirmation.taskConflicts}
          onCancel={editor.cancelSave}
          onConfirm={editor.confirmSave}
        />
      )}
      {crmDisconnectedModal}
    </ContractInformationPage>
  );
};

export default SpecialConditions;
